import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, Send, User, MessageSquare, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    // Let Netlify handle the form submission
    // For demo purposes, show success message
    if (!e.target.checkValidity()) {
      return;
    }
    // In production, Netlify will handle this automatically
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors w-fit">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">トップページに戻る</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-600 rounded-lg">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">お問い合わせ</h1>
          </div>
          <p className="text-gray-600">
            当サイトへのご質問、ご意見、ご要望などがございましたら、下記フォームよりお気軽にお問い合わせください。
          </p>
        </div>

        {submitted ? (
          <Card className="border-2 border-green-200 shadow-md">
            <CardContent className="py-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">送信完了</h2>
                <p className="text-gray-600 mb-6">
                  お問い合わせありがとうございます。<br />
                  内容を確認の上、折り返しご連絡いたします。
                </p>
                <Link 
                  to="/" 
                  className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  トップページに戻る
                </Link>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border border-gray-100 shadow-md">
            <CardHeader>
              <CardTitle className="text-lg text-gray-800">お問い合わせフォーム</CardTitle>
            </CardHeader>
            <CardContent>
              <form 
                name="contact" 
                method="POST" 
                data-netlify="true"
                netlify-honeypot="bot-field"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* Netlify form hidden inputs */}
                <input type="hidden" name="form-name" value="contact" />
                <p className="hidden">
                  <label>
                    Don't fill this out if you're human: <input name="bot-field" />
                  </label>
                </p>

                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="flex items-center gap-2 text-gray-700">
                    <User className="w-4 h-4 text-blue-600" />
                    お名前 <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="山田 太郎"
                    className="h-12 border-2 border-gray-200 focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2 text-gray-700">
                    <Mail className="w-4 h-4 text-blue-600" />
                    メールアドレス <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="example@email.com"
                    className="h-12 border-2 border-gray-200 focus:border-blue-500 transition-colors"
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <Label htmlFor="message" className="flex items-center gap-2 text-gray-700">
                    <MessageSquare className="w-4 h-4 text-blue-600" />
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    placeholder="お問い合わせ内容をご記入ください..."
                    className="border-2 border-gray-200 focus:border-blue-500 transition-colors resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button 
                  type="submit"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-medium text-lg transition-colors"
                >
                  <Send className="w-5 h-5 mr-2" />
                  送信する
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  ※ 通常2〜3営業日以内にご返信いたします。
                </p>
              </form>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

const Footer = () => (
  <footer className="bg-gray-50 border-t border-gray-200 mt-12">
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-sm text-gray-500">© 2026 Silent Code.</p>
        <nav className="flex items-center gap-6">
          <Link to="/privacy" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">プライバシーポリシー</Link>
          <Link to="/about" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">運営者情報</Link>
          <Link to="/contact" className="text-sm text-gray-500 hover:text-blue-600 transition-colors">お問い合わせ</Link>
        </nav>
      </div>
    </div>
  </footer>
);

export default ContactPage;
